/**
 * Per-project logo widget for the operator dashboard.
 *
 * Displays the current brand logo (read from project state's
 * `branding.logoUrl`, served from the staging hostname so it works regardless
 * of whether a custom domain is connected) and provides a "Replace logo"
 * button that POSTs a new image to /api/projects/:id/logo. The endpoint
 * writes to BOTH workspace/assets/images/ AND projects/<id>/uploads/ (the
 * persistent location restored by `ensureWorkspaceLogo()` at the start of
 * each render phase), updates BrandProfile.data.branding.{logoUrl,
 * logoWidth, logoHeight} in place, and (when the operator confirms) rolls
 * the latest BuildRun back to lastCompletedPhase='media' + re-enqueues it
 * with autoDeploy=true so the live site picks up the new logo.
 *
 * UX flow:
 *   1. User picks a file → confirm dialog explaining the ~3 min rebuild.
 *   2. On confirm: upload, swap the preview to the new logoUrl
 *      immediately, then sit in a "Rebuilding…" state.
 *   3. Polling: we piggyback on the existing `projectState` query (which
 *      already auto-refetches every 2 s during running/queued BuildRuns —
 *      see ProjectDetail.tsx and SiteCard.tsx), invalidating it on upload
 *      so the parent picks up status='running'. When status transitions
 *      back to 'completed' (or 'failed'), the card exits loading.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Upload, AlertCircle, Loader2 } from 'lucide-react';
import { api, ApiError, type BrandingSummary, type BuildRun } from '../api/client';
import { useToast } from './Toast';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/Dialog';

interface Props {
  projectId: string;
  brandId: string;
  branding?: BrandingSummary;
  /** Latest BuildRun, used to detect rebuild completion. */
  buildRun?: BuildRun;
}

const LOGO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif', 'avif'] as const;
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

function getLogoFormat(file: File): string {
  const nameExt = file.name.split('.').pop()?.toLowerCase();
  if (nameExt && (LOGO_EXTENSIONS as readonly string[]).includes(nameExt)) return nameExt;
  const m = file.type.toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpeg';
  if (m.includes('svg')) return 'svg';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  if (m.includes('avif')) return 'avif';
  return 'png';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function LogoCard({ projectId, brandId, branding, buildRun }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Pending file held between file-picker change and confirm-dialog accept.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The BuildRun id we just kicked off — drives the "Rebuilding…" indicator
  // until that BuildRun reports completed/failed via the projectState poll.
  const [watchingBuildRunId, setWatchingBuildRunId] = useState<string | null>(null);

  // Optimistic logo URL — set immediately on upload success so the preview
  // doesn't keep showing the stale image while the rebuild grinds.
  const [overrideLogoUrl, setOverrideLogoUrl] = useState<string | null>(null);

  const logoUrl = overrideLogoUrl ?? branding?.logoUrl ?? null;
  // The staging hostname always exists post-create; using it (instead of the
  // possibly-null custom domain) means the logo preview keeps working even
  // before a domain is connected.
  const stagingOrigin = `https://${brandId}.sites.siteskick.com`;
  const fullLogoUrl =
    logoUrl && /^https?:\/\//i.test(logoUrl)
      ? logoUrl
      : logoUrl
      ? `${stagingOrigin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`
      : null;

  // Detect rebuild completion. The parent's projectState query polls every 2s
  // while a build is running/queued — we just watch the prop here and exit
  // the loading state when our BuildRun transitions to a terminal status.
  useEffect(() => {
    if (!watchingBuildRunId) return;
    if (!buildRun || buildRun.id !== watchingBuildRunId) return;
    if (buildRun.status === 'completed') {
      toast.success('Logo updated and site republished');
      setWatchingBuildRunId(null);
    } else if (buildRun.status === 'failed') {
      toast.error(
        `Rebuild failed${buildRun.errorCode ? ` (${buildRun.errorCode})` : ''} — logo file is saved; try a manual resume`,
      );
      setWatchingBuildRunId(null);
    }
    // We intentionally don't depend on `toast` (stable ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchingBuildRunId, buildRun?.id, buildRun?.status, buildRun?.errorCode]);

  const isRebuilding = watchingBuildRunId !== null;
  const busy = uploading || isRebuilding;

  function pick() {
    fileInput.current?.click();
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice re-fires onChange.
    if (e.target.value) e.target.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo is too large (max ${Math.round(MAX_LOGO_BYTES / 1024 / 1024)} MB)`);
      return;
    }
    setPendingFile(file);
    setConfirmOpen(true);
  }

  async function handleConfirm(): Promise<void> {
    const file = pendingFile;
    setConfirmOpen(false);
    if (!file) return;
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const format = getLogoFormat(file);
      const res = await api.replaceLogo(projectId, fileBase64, format, { autoRebuild: true });
      // Swap the preview immediately so the card doesn't look stale.
      setOverrideLogoUrl(res.logoUrl);
      // Invalidate so ProjectDetail (the parent) picks up the new
      // branding + the now-running BuildRun status on its next refetch.
      qc.invalidateQueries({ queryKey: ['projectState', projectId] });
      if (res.rebuildTriggered && res.buildRunId) {
        setWatchingBuildRunId(res.buildRunId);
        toast.success('Logo uploaded — rebuilding site (about 3 minutes)');
      } else {
        // No BuildRun to resume (e.g. project pre-domain-config) — just
        // confirm the upload and leave it to the next manual build.
        toast.success(
          res.prevLogoUrl
            ? 'Logo replaced — next build will use the new logo'
            : 'Logo uploaded — next build will use it',
        );
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message ?? String(err);
      toast.error(`Failed to upload logo: ${msg}`);
    } finally {
      setUploading(false);
      setPendingFile(null);
    }
  }

  function handleCancel(): void {
    setConfirmOpen(false);
    setPendingFile(null);
  }

  return (
    <>
      <Card>
        <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="h-20 w-20 shrink-0 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden">
              {fullLogoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={fullLogoUrl}
                  alt="Project logo"
                  className="h-full w-full object-contain"
                  onError={(ev) => {
                    // Logo file might be missing on disk on the staging origin
                    // (rare — the dual-write ensures persistence). Fall back to
                    // the placeholder icon so the UI doesn't show a broken
                    // image glyph.
                    (ev.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <ImageIcon className="h-7 w-7 text-zinc-300" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[11.5px] uppercase tracking-wide text-zinc-400 mb-1">
                Logo
              </div>
              {isRebuilding ? (
                <div className="flex items-center gap-2 text-[13px] text-blue-700">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Rebuilding site… ~3 minutes</span>
                </div>
              ) : logoUrl ? (
                <>
                  <div className="text-[13px] font-medium text-zinc-900 truncate">
                    {logoUrl.split('/').pop()}
                  </div>
                  {branding?.logoWidth && branding?.logoHeight ? (
                    <div className="text-[12px] text-zinc-500 mt-0.5">
                      {branding.logoWidth} x {branding.logoHeight}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-[13px] text-zinc-500 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                  No logo set
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />
            <Button
              onClick={pick}
              loading={busy}
              disabled={busy}
              variant={logoUrl ? 'outline' : 'primary'}
              size="md"
              leftIcon={<Upload className="h-3.5 w-3.5" />}
            >
              {logoUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload this logo and rebuild the site?</DialogTitle>
            <DialogDescription>
              The live site will be republished with the new logo. This takes
              about 3 minutes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void handleConfirm();
              }}
            >
              Upload & rebuild
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
